import { defineStore } from 'pinia';
import { api, deviceToken, type GameConfig, type PaytablePrize, type SpinResult } from '../lib/api';
import { popTicketForPlay } from '../lib/offlineDb';
import { startOfflineSync, subscribe, isOnline } from '../lib/offlineSync';

type Screen = 'setup' | 'game' | 'admin';

// Recent spins shown in the in-game History panel. Capped to keep localStorage
// bounded; the dashboard's spin log is the authoritative record.
const RECENT_KEY = 'wsb_recent_spins';
const RECENT_CAP = 20;

function recordRecentSpin(spin: SpinResult) {
  try {
    const arr: SpinResult[] = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    arr.unshift(spin);
    localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, RECENT_CAP)));
  } catch {
    /* localStorage full or quota — drop silently */
  }
}

export const useGameStore = defineStore('game', {
  state: () => ({
    screen: 'setup' as Screen,
    config: null as GameConfig | null,
    paytable: [] as PaytablePrize[],
    lastSpin: null as SpinResult | null,
    spinning: false,
    loadError: null as string | null,
    online: true,
    ticketsRemaining: 0,
    redemptionsPending: 0,
  }),
  actions: {
    async init() {
      if (!deviceToken()) {
        this.screen = 'setup';
        return;
      }
      // Listen to sync state so the UI can show the ticket badge.
      subscribe((s) => {
        this.online = s.online;
        this.ticketsRemaining = s.ticketsRemaining;
        this.redemptionsPending = s.redemptionsPending;
      });
      // Start sync first so we have tickets even if config fetch fails
      // (e.g. flaky network on pairing day).
      startOfflineSync();

      try {
        this.config = await api.config();
        await this.refreshPaytable();
        this.screen = 'game';
      } catch (e) {
        // Offline / unreachable: still try to enter the game if we have any
        // cached tickets — SW will serve last-known config & paytable.
        this.loadError = e instanceof Error ? e.message : 'load_failed';
        // The SW StaleWhileRevalidate for /paytable means a second attempt
        // hits cache; for /config we just degrade gracefully.
        try {
          this.config = await api.config();
        } catch {
          /* truly offline first-run — show setup */
        }
        this.screen = this.config ? 'game' : 'setup';
      }
    },
    async refreshPaytable() {
      try {
        const res = await api.paytable();
        this.paytable = res.prizes;
      } catch {
        // Offline: keep whatever we have. SW may still serve cached version.
      }
    },
    // Spin flow: prefer an offline ticket so latency is zero AND we work
    // without a network. Fall back to the live online endpoint when no
    // tickets are queued (rare, but covers first-ever-spin before top-up).
    async spin(): Promise<SpinResult> {
      this.spinning = true;
      try {
        const ticket = await popTicketForPlay();
        let result: SpinResult;
        if (ticket) {
          result = {
            won: !!ticket.won,
            prize: ticket.prize,
            spin_id: 0,
            server_time: Math.floor(Date.now() / 1000),
          };
        } else if (!isOnline()) {
          throw new Error('offline_no_tickets');
        } else {
          result = await api.spin();
        }
        this.lastSpin = result;
        recordRecentSpin(result);
        return result;
      } finally {
        this.spinning = false;
      }
    },
    enterAdmin() {
      this.screen = 'admin';
    },
    exitAdmin() {
      this.screen = 'game';
    },
  },
});
